// Central de Ajuda (leitura) — as quatro áreas pedidas: Comece aqui, Minha função, Manuais por módulo e
// Busca. O conteúdo já chega filtrado pelo backend conforme o ambiente + permissões do usuário; clientes e
// entregadores só recebem o conteúdo do próprio ambiente. Mestre-detalhe na mesma tela (sem perder a Central).
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, BookOpen, Compass, Loader2, Search, UserCog } from "lucide-react";
import {
  fetchHelpArticle, fetchHelpSections, searchHelp,
  type HelpArticleDetail, type HelpArticleSummary, type HelpSections,
} from "@/lib/helpApi";
import { HelpArticleBody } from "@/components/help/HelpArticleBody";

export default function HelpCenter() {
  const navigate = useNavigate();
  const [sections, setSections] = useState<HelpSections | null>(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<HelpArticleSummary[] | null>(null);
  const [selected, setSelected] = useState<HelpArticleDetail | null>(null);
  const [articleLoading, setArticleLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchHelpSections()
      .then((s) => { if (!cancelled) setSections(s); })
      .catch(() => { if (!cancelled) setSections({ comeceAqui: [], minhaFuncao: [], manuais: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults(null); return; }
    const t = window.setTimeout(() => {
      searchHelp(q).then(setResults).catch(() => setResults([]));
    }, 350);
    return () => window.clearTimeout(t);
  }, [query]);

  const openArticle = (slugOrId: string) => {
    // Abrir um artigo encerra a busca (limpa o termo), para alternar da lista de resultados para o artigo.
    setQuery("");
    setResults(null);
    setArticleLoading(true);
    fetchHelpArticle(slugOrId)
      .then(setSelected)
      .catch(() => setSelected(null))
      .finally(() => setArticleLoading(false));
  };

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-6">
      <div className="mb-6 flex items-center gap-3">
        <BookOpen className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-xl font-bold">Central de Ajuda e Treinamento</h1>
          <p className="text-sm text-muted-foreground">Orientações das rotinas e do seu ambiente.</p>
        </div>
      </div>

      <div className="relative mb-6 max-w-xl">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Pesquisar por assunto, rotina, botão ou mensagem de erro"
          className="w-full rounded-lg border border-border bg-background py-2 pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {results !== null ? (
        <section>
          <h2 className="mb-2 text-sm font-semibold text-muted-foreground">Resultados da busca</h2>
          <ArticleGrid articles={results} onPick={openArticle} empty="Nenhum artigo encontrado." />
        </section>
      ) : selected || articleLoading ? (
        <div className="rounded-xl border border-border bg-background p-4">
          <button type="button" onClick={() => setSelected(null)}
            className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Voltar à Central
          </button>
          {articleLoading ? <Loading /> : selected ? (
            <HelpArticleBody article={selected} onNavigate={(p) => navigate(p)} onOpenArticle={openArticle} />
          ) : null}
        </div>
      ) : loading ? (
        <Loading />
      ) : (
        <div className="space-y-8">
          <Section icon={<Compass className="h-4 w-4 text-primary" />} title="Comece aqui">
            <ArticleGrid articles={sections?.comeceAqui ?? []} onPick={openArticle}
              empty="Sem artigos de primeiros passos por enquanto." />
          </Section>
          <Section icon={<UserCog className="h-4 w-4 text-primary" />} title="Minha função">
            <ArticleGrid articles={sections?.minhaFuncao ?? []} onPick={openArticle}
              empty="Sem rotinas específicas da sua função por enquanto." />
          </Section>
          <Section icon={<BookOpen className="h-4 w-4 text-primary" />} title="Manuais por módulo">
            {(sections?.manuais ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">Sem manuais disponíveis para o seu acesso.</p>
            ) : (
              <div className="space-y-5">
                {sections!.manuais.map((group) => (
                  <div key={group.moduleKey}>
                    <h3 className="mb-2 text-sm font-semibold">{group.moduleKey}</h3>
                    <ArticleGrid articles={group.articles} onPick={openArticle} empty="" />
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      )}
    </div>
  );
}

function Section({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="mb-3 flex items-center gap-2 text-base font-semibold">{icon}{title}</h2>
      {children}
    </section>
  );
}

function ArticleGrid({ articles, onPick, empty }: {
  articles: HelpArticleSummary[]; onPick: (slug: string) => void; empty: string;
}) {
  if (articles.length === 0) {
    return empty ? <p className="text-sm text-muted-foreground">{empty}</p> : null;
  }
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {articles.map((a) => (
        <button key={a.id} type="button" onClick={() => onPick(a.slug)}
          className="rounded-xl border border-border bg-background p-4 text-left transition-colors hover:border-primary/50 hover:bg-muted/40">
          <p className="font-medium leading-tight">{a.title}</p>
          {a.summary ? <p className="mt-1 text-sm text-muted-foreground line-clamp-3">{a.summary}</p> : null}
        </button>
      ))}
    </div>
  );
}

function Loading() {
  return (
    <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin" /> Carregando a Central…
    </div>
  );
}
